import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  Button,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Field,
  Input,
  OverlayDrawer,
  Select,
} from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { ContractNegotiation } from '../../../models/Entities/Contracts/ContractNegotiation';
import { NewContractNegotiation } from '../../../models/Entities/Contracts/NewContractNegotiation';
import { Sponsor } from '../../../models/Entities/Sponsor/Sponsor';
import { SponsorSelectorComponent } from '../../../commons/component/Inputs/SponsorSelector/SponsorSelectorComponet';
import { useContractNegotiationFormStyles } from './ContractNegotiationForm.Styles';
import { AppState } from '../../../redux/reducers';

interface OwnProps {
  contractId: number;
  contractAddendumId: number;
  negotiation?: ContractNegotiation;
  isOpen: boolean;
  onDismiss: () => void;
  onSave: (entity: NewContractNegotiation | ContractNegotiation) => void;
  defaultSponsorId?: number;
}

interface ConnectedProps {
  sponsors: Sponsor[];
}

type Props = OwnProps & ConnectedProps;

const toDateInputValue = (date: Date | undefined | null): string => {
  if (!date) return '';
  const d = new Date(date as Date);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};

const ContractNegotiationFormComponent = (props: Props) => {
  const { t } = useTranslation();
  const styles = useContractNegotiationFormStyles();

  const [sponsorId, setSponsorId] = useState<string>('');
  const [sponsorName, setSponsorName] = useState<string>('');
  const [negotiationStart, setNegotiationStart] = useState<string>('');
  const [negotiationEnd, setNegotiationEnd] = useState<string>('');
  const [negotiationStatus, setNegotiationStatus] = useState<number>(0);

  const [sponsorError, setSponsorError] = useState<string>('');

  useEffect(() => {
    if (props.isOpen) {
      setSponsorError('');
      if (props.negotiation) {
        // Editing existing: use saved sponsor data
        setSponsorId(props.negotiation.sponsorId || '');
        setSponsorName(props.negotiation.sponsorName || '');
      } else if (props.defaultSponsorId) {
        // Creating new with a known default sponsor: resolve directly from the store
        const sponsor = props.sponsors.find((s) => s.id === props.defaultSponsorId);
        if (sponsor) {
          setSponsorId(sponsor.person?.personIdentifier?.trim() || String(sponsor.id));
          setSponsorName(sponsor.person?.businessName || '');
        } else {
          // Sponsors not loaded yet — selector's auto-init will call handleSponsorChange
          setSponsorId('');
          setSponsorName('');
        }
      } else {
        setSponsorId('');
        setSponsorName('');
      }
      setNegotiationStart(toDateInputValue(props.negotiation?.negotiationStart));
      setNegotiationEnd(toDateInputValue(props.negotiation?.negotiationEnd));
      setNegotiationStatus(
        typeof props.negotiation?.negotiationStatus === 'number'
          ? props.negotiation.negotiationStatus
          : 0
      );
    }
  }, [props.isOpen, props.negotiation, props.defaultSponsorId, props.sponsors]);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleSponsorChange = (selected: Sponsor | Sponsor[]) => {
    const sponsor = Array.isArray(selected) ? selected[0] : selected;
    if (sponsor) {
      const id = sponsor.person?.personIdentifier?.trim() || String(sponsor.id);
      setSponsorId(id);
      setSponsorName(sponsor.person?.businessName || '');
    } else {
      setSponsorId('');
      setSponsorName('');
    }
  };

  const handleSave = () => {
    if (!sponsorId) {
      setSponsorError(t('contractNegotiationForm.validation.sponsorRequired', 'Debe seleccionar un sponsor.'));
      return;
    }
    setSponsorError('');
    const base: NewContractNegotiation = {
      contractId: props.contractId,
      contractAddendumId: props.contractAddendumId,
      sponsorId: sponsorId || undefined,
      sponsorName: sponsorName || undefined,
      negotiationStart: negotiationStart ? new Date(negotiationStart) : undefined,
      negotiationEnd: negotiationEnd ? new Date(negotiationEnd) : undefined,
      negotiationStatus,
    };

    if (props.negotiation?.id) {
      props.onSave({ ...props.negotiation, ...base });
    } else {
      props.onSave(base);
    }
  };

  const isEdit = !!props.negotiation?.id;
  const dialogTitle = isEdit
    ? t('contractNegotiationForm.titleEdit', 'Editar negociación')
    : t('contractNegotiationForm.titleNew', 'Nueva negociación');

  return (
    <OverlayDrawer position="end" size="medium" open={props.isOpen}
      onOpenChange={(_, { open }) => { if (!open) props.onDismiss(); }}>
      <DrawerHeader>
        <DrawerHeaderTitle
          action={<Button appearance="subtle" icon={<DismissRegular />} onClick={props.onDismiss} />}>
          {dialogTitle}
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody>
        <div className={styles.form}>
          <div className={styles.formRow}>
            <Field
              label={t('contractNegotiationForm.fields.sponsor', 'Sponsor')}
              required
              validationState={sponsorError ? 'error' : 'none'}
              validationMessage={sponsorError || undefined}
            >
              <SponsorSelectorComponent
                key={`sponsor-selector-${props.isOpen}-${props.negotiation?.id ?? 'new'}`}
                selectionLimit={1}
                onChangeSelection={handleSponsorChange}
                initialSponsorId={
                  props.negotiation
                    ? (props.negotiation.sponsorId ? Number(props.negotiation.sponsorId) : undefined)
                    : props.defaultSponsorId
                }
              />
            </Field>
          </div>
          <div className={styles.formRow}>
            <Field label={t('contractNegotiationForm.fields.negotiationStart', 'Fecha de inicio')}>
              <Input
                type="date"
                value={negotiationStart}
                onChange={(_, data) => setNegotiationStart(data.value)}
              />
            </Field>
            <Field label={t('contractNegotiationForm.fields.negotiationEnd', 'Fecha de fin')}>
              <Input
                type="date"
                value={negotiationEnd}
                onChange={(_, data) => setNegotiationEnd(data.value)}
              />
            </Field>
          </div>
          <div className={styles.formRow}>
            <Field label={t('contractNegotiationForm.fields.negotiationStatus', 'Estado')}>
              <Select
                value={String(negotiationStatus)}
                onChange={(_, data) => setNegotiationStatus(Number(data.value))}
              >
                <option value="0">{t('contractNegotiationForm.status.pending', 'Pendiente')}</option>
                <option value="1">{t('contractNegotiationForm.status.inProgress', 'En curso')}</option>
                <option value="2">{t('contractNegotiationForm.status.finished', 'Finalizada')}</option>
                <option value="3">{t('contractNegotiationForm.status.cancelled', 'Cancelada')}</option>
              </Select>
            </Field>
          </div>
        </div>
        <div className={styles.formActions}>
          <Button appearance="secondary" onClick={props.onDismiss}>
            {t('contractNegotiationForm.actions.cancel', 'Cancelar')}
          </Button>
          <Button appearance="primary" onClick={handleSave}>
            {t('contractNegotiationForm.actions.save', 'Guardar')}
          </Button>
        </div>
      </DrawerBody>
    </OverlayDrawer>
  );
};

const mapStateToProps = (state: AppState): ConnectedProps => ({
  sponsors: state.Sponsor?.list ?? [],
});

export default connect(mapStateToProps)(ContractNegotiationFormComponent as React.ComponentType<OwnProps>);
